import { useCallback, useEffect, useRef, useState } from 'react';
import { Wand2, ImagePlus, Video, X, Loader2, Check, Layers, LogIn, LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import { useAgentStore } from '@/store/agent-store';
import {
  DEFAULT_IMAGE_SLOT_OPTIONS, DEFAULT_AUDIO_SLOT_OPTIONS, GENERATION_MODE_OPTIONS,
} from '@/data/slot-config';
import {
  uploadAssetFile, uploadVideo, uploadEndcard, getImageProviderStatuses,
  startJimengLogin, checkJimengLogin, logoutJimeng, type ImageProviderStatuses,
} from '@/api/modules/assets';
import { updateSessionImageProvider } from '@/api/modules/sessions';
import { BatchConfig } from '@/components/batch-config';
import { Select } from '@/components/ui/select';
import { useConfirm } from '@/components/ui/confirm-dialog';

const IMAGE_PROVIDER_OPTIONS = [
  { value: 'jimeng', label: '即梦' },
  { value: 'wanxiang', label: '通义万相' },
];

interface PendingFile {
  uid: string; file: File; name: string; type: 'image' | 'audio';
  slotName: string; preview?: string; assetId?: string; uploading?: boolean;
}
interface VideoFile { filePath: string; fileName: string; duration: number; }
interface EndcardFile { filePath: string; fileName: string; }

interface GenerationConfigBarProps { sessionId?: string | null; }

/**
 * 生成配置栏：生成模式选择 + 风格参考图 + 视频/结束页(video) + 上传素材槽位映射。
 * 配置写入 store.genConfig；确认时随 GDD 一并提交（对接 P0 后端）。
 */
export function GenerationConfigBar({ sessionId }: GenerationConfigBarProps): JSX.Element {
  const genConfig = useAgentStore((s) => s.genConfig);
  const setGenConfig = useAgentStore((s) => s.setGenConfig);
  const mode = genConfig.generationMode ?? 'auto';
  const imageProvider = genConfig.imageProvider ?? 'jimeng';

  const [files, setFiles] = useState<PendingFile[]>([]);
  const [refUrl, setRefUrl] = useState<string>('');
  const [refUploading, setRefUploading] = useState(false);
  const [videos, setVideos] = useState<VideoFile[]>([]);
  const [endcards, setEndcards] = useState<EndcardFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [providerStatuses, setProviderStatuses] = useState<ImageProviderStatuses | null>(null);
  const [providerChecking, setProviderChecking] = useState(false);
  const [loginDeviceCode, setLoginDeviceCode] = useState<string | null>(null);
  const [loginInstalling, setLoginInstalling] = useState(false);
  const batchConfig = genConfig.batchConfig;
  const batchCount = batchConfig?.count ?? 0;
  const { confirm, dialog } = useConfirm();

  const assetRef = useRef<HTMLInputElement>(null);
  const refImgRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const endcardRef = useRef<HTMLInputElement>(null);

  const refreshProviderStatuses = useCallback(async (force = false): Promise<void> => {
    setProviderChecking(true);
    try { setProviderStatuses(await getImageProviderStatuses(force)); }
    catch { /* 状态探测失败不阻断会话输入 */ }
    finally { setProviderChecking(false); }
  }, []);

  useEffect(() => { void refreshProviderStatuses(); }, [refreshProviderStatuses]);

  useEffect(() => {
    if (!loginDeviceCode) return;
    let stopped = false;
    const poll = async () => {
      try {
        const result = await checkJimengLogin(loginDeviceCode);
        if (stopped) return;
        if (result.authenticated) {
          setLoginDeviceCode(null);
          await refreshProviderStatuses(true);
          toast.success('即梦登录成功');
        } else if (!result.pending) {
          setLoginDeviceCode(null);
          toast.error(result.message ?? '即梦登录失败');
        }
      } catch { /* Device Flow 短暂查询失败，下轮继续 */ }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 2000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [loginDeviceCode, refreshProviderStatuses]);

  const doJimengLogin = async (): Promise<void> => {
    setLoginInstalling(true);
    try {
      // 后端会在 CLI 未安装时先自动安装（耗时较长），安装完成后返回授权地址再跳转。
      const flow = await startJimengLogin();
      if (flow.authenticated) {
        await refreshProviderStatuses(true);
        toast.success('即梦已登录');
        return;
      }
      if (!flow.verificationUri || !flow.deviceCode) throw new Error(flow.message ?? '未获取到授权地址');
      window.open(flow.verificationUri, '_blank', 'noopener,noreferrer');
      setLoginDeviceCode(flow.deviceCode);
      toast.info('请在打开的即梦页面完成授权');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '无法发起即梦登录');
    } finally {
      setLoginInstalling(false);
    }
  };

  const handleJimengLogin = async (): Promise<void> => {
    try {
      // 先强制探测 CLI 安装状态：未安装时弹窗征得用户同意后再自动安装。
      const statuses = await getImageProviderStatuses(true);
      setProviderStatuses(statuses);
      if (!statuses.jimeng.installed) {
        confirm(
          '安装即梦 CLI',
          '当前未检测到即梦 CLI（dreamina）。是否立即为您自动安装？安装完成后将自动跳转登录授权页。',
          () => { void doJimengLogin(); },
          'default',
        );
        return;
      }
      await doJimengLogin();
    } catch {
      // 探测失败不阻断：直接尝试登录（后端仍会自动兜底安装）。
      await doJimengLogin();
    }
  };

  const handleJimengLogout = async (): Promise<void> => {
    try {
      await logoutJimeng();
      await refreshProviderStatuses(true);
      toast.success('已退出即梦登录');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '退出即梦登录失败');
    }
  };

  // ── 上传素材（带槽位） ──
  const addFiles = (selected: FileList): void => {
    const added: PendingFile[] = Array.from(selected).map((f) => {
      const isImage = f.type.startsWith('image/');
      return {
        uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file: f, name: f.name, type: isImage ? 'image' : 'audio',
        slotName: isImage ? 'background' : 'bgmSrc',
        preview: isImage ? URL.createObjectURL(f) : undefined,
      };
    });
    setFiles((prev) => [...prev, ...added]);
  };

  const removeFile = (uid: string): void =>
    setFiles((prev) => {
      const f = prev.find((x) => x.uid === uid);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      const next = prev.filter((x) => x.uid !== uid);
      setGenConfig({ customAssets: next.map((x) => x.assetId).filter(Boolean) as string[] });
      return next;
    });

  const setSlot = (uid: string, slotName: string): void =>
    setFiles((prev) => prev.map((f) => (f.uid === uid ? { ...f, slotName } : f)));

  /** 上传所有未上传的素材，返回 assetId 列表并同步进 genConfig。 */
  const flushUploads = async (): Promise<void> => {
    const pending = files.filter((f) => !f.assetId);
    if (!pending.length) return;
    setBusy(true);
    setFiles((prev) => prev.map((f) => (f.assetId ? f : { ...f, uploading: true })));
    try {
      const results = await Promise.allSettled(
        pending.map(async (f) => {
          const { id } = await uploadAssetFile(f.file, {
            category: f.type === 'audio' ? 'audio' : 'custom',
            slotName: f.slotName, sessionId: sessionId ?? undefined,
          });
          return { uid: f.uid, id };
        }),
      );
      setFiles((prev) => {
        const next = prev.map((f) => {
          const r = results.find((x) => x.status === 'fulfilled' && x.value.uid === f.uid);
          return r && r.status === 'fulfilled' ? { ...f, assetId: r.value.id, uploading: false } : { ...f, uploading: false };
        });
        setGenConfig({ customAssets: next.map((x) => x.assetId).filter(Boolean) as string[] });
        return next;
      });
      if (results.some((r) => r.status === 'rejected')) toast.error('部分素材上传失败');
    } finally {
      setBusy(false);
    }
  };

  // ── 风格参考图 ──
  const handleRefImg = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.warning('参考图必须是图片'); return; }
    setRefUploading(true);
    try {
      const { url } = await uploadAssetFile(file, { category: 'reference', slotName: 'style_reference', sessionId: sessionId ?? undefined });
      if (url) { setRefUrl(url); setGenConfig({ referenceImageUrls: [url] }); toast.success('风格参考图已上传'); }
      else toast.warning('未获取到参考图地址');
    } catch { toast.error('参考图上传失败'); }
    finally { setRefUploading(false); }
  };

  const clearRef = (): void => { setRefUrl(''); setGenConfig({ referenceImageUrls: undefined }); };

  // ── 视频 & 结束页（video 模式） ──
  const handleVideo = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const list = e.target.files; e.target.value = '';
    if (!list?.length) return;
    setBusy(true);
    try {
      const uploaded: VideoFile[] = [];
      for (const f of Array.from(list)) uploaded.push(await uploadVideo(f));
      setVideos((prev) => {
        const next = [...prev, ...uploaded];
        setGenConfig({ videoFilePaths: next.map((v) => v.filePath) });
        return next;
      });
      toast.success(`${uploaded.length} 个视频已上传`);
    } catch { toast.error('视频上传失败'); }
    finally { setBusy(false); }
  };

  const handleEndcard = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const ec = await uploadEndcard(file);
      setEndcards((prev) => {
        const next = [...prev, ec];
        setGenConfig({ endCardFilePaths: next.map((x) => x.filePath) });
        return next;
      });
      toast.success('结束页图片已上传');
    } catch { toast.error('结束页上传失败'); }
    finally { setBusy(false); }
  };

  const removeVideo = (fp: string): void =>
    setVideos((prev) => { const next = prev.filter((v) => v.filePath !== fp); setGenConfig({ videoFilePaths: next.map((v) => v.filePath) }); return next; });
  const removeEndcard = (fp: string): void =>
    setEndcards((prev) => { const next = prev.filter((v) => v.filePath !== fp); setGenConfig({ endCardFilePaths: next.map((v) => v.filePath) }); return next; });

  return (
    <div className="space-y-1.5">
      {/* Toolbar row */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* 生成模式 */}
        <div className="flex items-center gap-1 rounded-lg border border-border/50 bg-muted/30 px-2 py-1">
          <Wand2 size={12} className="text-neon-cyan" />
          <Select
            options={GENERATION_MODE_OPTIONS}
            value={mode}
            onChange={(val) => setGenConfig({ generationMode: val as typeof mode })}
            className="border-none bg-transparent px-1 py-0 text-sm hover:bg-transparent"
          />
        </div>

        {/* 图片生成引擎：会话级配置，默认即梦 */}
        <div className="flex items-center gap-1 rounded-lg border border-border/50 bg-muted/30 px-2 py-1">
          <ImagePlus size={12} className="text-neon-purple" />
          <span className="text-[11px] text-muted-foreground">生图</span>
          <Select
            options={IMAGE_PROVIDER_OPTIONS}
            value={imageProvider}
            onChange={(val) => {
              const provider = val as 'jimeng' | 'wanxiang';
              setGenConfig({ imageProvider: provider });
              window.localStorage.setItem('playgen:image-provider:last', provider);
              if (sessionId) {
                void updateSessionImageProvider(sessionId, provider).catch(() => {
                  // 本地选择仍保留；网络恢复后下一次确认生成会再次写入会话配置。
                });
              }
              const status = providerStatuses?.[provider];
              if (status && !status.available) toast.warning(status.message ?? `${provider === 'jimeng' ? '即梦' : '通义万相'}当前不可用`);
            }}
            className="border-none bg-transparent px-1 py-0 text-sm hover:bg-transparent"
          />
          <span
            className={cn('h-1.5 w-1.5 rounded-full', providerStatuses?.[imageProvider]?.available ? 'bg-emerald-400' : 'bg-amber-400')}
            title={providerStatuses?.[imageProvider]?.message ?? '生图引擎可用'}
          />
          <button
            type="button"
            onClick={() => void refreshProviderStatuses(true)}
            className="text-muted-foreground hover:text-foreground"
            title="刷新生图引擎状态"
          >
            <RefreshCw size={11} className={cn(providerChecking && 'animate-spin')} />
          </button>
        </div>

        {imageProvider === 'jimeng' && providerStatuses && !providerStatuses.jimeng.authenticated && (
          <button
            type="button"
            onClick={() => void handleJimengLogin()}
            disabled={Boolean(loginDeviceCode) || loginInstalling}
            className="flex items-center gap-1 rounded-lg border border-amber-400/40 px-2 py-1 text-xs text-amber-400 disabled:opacity-60"
            title={providerStatuses.jimeng.message ?? '登录即梦'}
          >
            {loginDeviceCode || loginInstalling ? <Loader2 size={12} className="animate-spin" /> : <LogIn size={12} />}
            {loginDeviceCode ? '等待授权' : loginInstalling ? '安装即梦中…' : '登录即梦'}
          </button>
        )}

        {imageProvider === 'jimeng' && providerStatuses?.jimeng.authenticated && (
          <button
            type="button"
            onClick={() => confirm('退出即梦', '是否要清空即梦cli授权，退出后即梦生图功能将不可用。', () => { void handleJimengLogout(); })}
            className="flex items-center gap-1 rounded-lg border border-border/50 px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            title="退出即梦登录"
          >
            <LogOut size={12} />退出即梦
          </button>
        )}

        {/* 批量设置 — 暂时隐藏，功能已迁移至独立的批量管理页面 */}
        {false && (
        <button onClick={() => setBatchOpen(true)}
          className={cn('icon-btn relative flex items-center gap-1 rounded-lg border px-2.5 py-1 text-sm', batchCount > 0 ? 'border-neon-purple/60 text-neon-purple' : 'border-border/50 text-foreground/90')} title="批量生成设置">
          <Layers size={14} />批量设置
          {batchCount > 0 && <span className="ml-0.5 rounded-full bg-neon-purple/20 px-1 text-[10px] text-neon-purple">{batchCount}</span>}
        </button>
        )}

        {/* 上传素材 — 暂时隐藏，统一走右下角附件上传 */}
        {false && (
        <button onClick={() => assetRef.current?.click()} className="icon-btn flex items-center gap-1 rounded-lg border border-border/50 px-2.5 py-1 text-sm text-foreground/90" title="上传素材（可指定槽位）">
          <ImagePlus size={14} />上传素材
        </button>
        )}

        {/* 风格参考图 — 暂时隐藏，统一走右下角附件上传 */}
        {false && (
        <button onClick={() => refImgRef.current?.click()} disabled={refUploading}
          className={cn('icon-btn flex items-center gap-1 rounded-lg border px-2.5 py-1 text-sm', refUrl ? 'border-neon-cyan/60 text-neon-cyan' : 'border-border/50 text-foreground/90')} title="风格参考图">
          {refUploading ? <Loader2 size={14} className="animate-spin" /> : refUrl ? <Check size={14} /> : <ImagePlus size={14} />}风格参考图
        </button>
        )}

        {/* 视频 & 结束页（video 模式）*/}
        {mode === 'video' && (
          <>
            <button onClick={() => videoRef.current?.click()} disabled={busy}
              className="icon-btn flex items-center gap-1 rounded-lg border border-border/50 px-2.5 py-1 text-sm text-foreground/90" title="上传视频">
              <Video size={14} />视频
            </button>
            <button onClick={() => endcardRef.current?.click()} disabled={busy}
              className="icon-btn flex items-center gap-1 rounded-lg border border-border/50 px-2.5 py-1 text-sm text-foreground/90" title="上传结束页">
              <ImagePlus size={14} />结束页
            </button>
          </>
        )}
      </div>

      {/* 风格参考图预览 */}
      {refUrl && (
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <div className="relative h-10 w-10 overflow-hidden rounded border border-border/50">
            <img src={refUrl} alt="参考图" className="h-full w-full object-cover" />
            <button onClick={clearRef} className="absolute right-0 top-0 rounded-bl bg-black/60 p-0.5"><X size={9} className="text-white" /></button>
          </div>
          <span>风格参考图</span>
        </div>
      )}

      {/* 视频 tags */}
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {videos.map((v) => (
            <span key={v.filePath} className="flex items-center gap-1 rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] text-blue-400">
              {v.fileName}{v.duration ? ` (${v.duration.toFixed(1)}s)` : ''}
              <button onClick={() => removeVideo(v.filePath)}><X size={9} /></button>
            </span>
          ))}
        </div>
      )}
      {endcards.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {endcards.map((e) => (
            <span key={e.filePath} className="flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-400">
              {e.fileName}
              <button onClick={() => removeEndcard(e.filePath)}><X size={9} /></button>
            </span>
          ))}
        </div>
      )}

      {/* 上传素材列表 + 槽位 */}
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f) => (
            <div key={f.uid} className="flex items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-2 py-1">
              {f.preview ? (
                <img src={f.preview} alt={f.name} className="h-7 w-7 shrink-0 rounded object-cover" />
              ) : (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground"><ImagePlus size={12} /></div>
              )}
              <span className="min-w-0 flex-1 truncate text-[10px] text-foreground/80">{f.name}</span>
              <Select
                options={f.type === 'image' ? DEFAULT_IMAGE_SLOT_OPTIONS : DEFAULT_AUDIO_SLOT_OPTIONS}
                value={f.slotName}
                onChange={(val) => setSlot(f.uid, val)}
                className="max-w-[120px] rounded border-border/50 bg-background px-1.5 py-0.5 text-[10px]"
              />
              {f.uploading ? <Loader2 size={11} className="animate-spin text-neon-cyan" />
                : f.assetId ? <Check size={11} className="text-neon-green" />
                : <button onClick={() => removeFile(f.uid)} className="text-muted-foreground hover:text-red-400"><X size={11} /></button>}
            </div>
          ))}
          <button onClick={flushUploads} disabled={busy || files.every((f) => f.assetId)}
            className="glow-btn w-full rounded-lg py-1 text-[10px] font-medium disabled:opacity-40">
            {busy ? '上传中...' : '上传并绑定槽位'}
          </button>
        </div>
      )}

      {/* 批量设置弹窗 */}
      {batchOpen && (
        <BatchConfig
          initial={batchConfig ?? null}
          onClose={() => setBatchOpen(false)}
          onConfirm={(cfg) => {
            setGenConfig({ batchConfig: cfg.count > 0 ? cfg : undefined });
            setBatchOpen(false);
            if (cfg.count > 0) toast.success(`已配置批量生成 ${cfg.count} 个变体`);
          }}
        />
      )}

      {/* Hidden inputs */}
      <input ref={assetRef} type="file" multiple accept="image/*,audio/*" className="hidden" onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
      <input ref={refImgRef} type="file" accept="image/*" className="hidden" onChange={handleRefImg} />
      <input ref={videoRef} type="file" multiple accept="video/*" className="hidden" onChange={handleVideo} />
      <input ref={endcardRef} type="file" accept="image/*" className="hidden" onChange={handleEndcard} />

      {/* 退出即梦确认弹窗 */}
      {dialog}
    </div>
  );
}
